import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { apiAms, createApiClient, handleGetRequest } from "../apiService";
import { editMetadataTypes } from "../articles/articlesTypes";
import { SuccesType } from "../review/reviewTypes";
import { useToken } from "../tokenUtils";
import {
  addBannerTypes,
  addLogoUrlTypes,
  addSupplierLogoTypes,
  createSupplierAdminSchema,
  editFooterTypes,
  editPublicationTypes,
  editSupplierTypes,
  initialFooterTypes,
  PublicationBannersTypes,
  PublicationContactTypes,
  PublicationPoliciesTypes,
  PublicationTheme,
  publicationTypes,
  requestPublicationTypes,
  supplierTypes,
  userRoleTypes,
  UsersCollection,
} from "./adminTypes";

const domain = getSubdomain();
const apiClient = createApiClient("");

const handleRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const apiClientPublication = createApiClient("admin/publications");
const apiAmsBase = apiAms("ext/");

const getPublications = async (): Promise<publicationTypes[]> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const publications = await handleGetRequest<publicationTypes[]>(
    apiClientPublication,
    "/",
    currentToken,
  );

  return publications;
};

const getPublicationBySlug = async (
  slug: string,
): Promise<publicationTypes> => {
  const response = await apiClientPublication.get<publicationTypes>(`/${slug}`);
  return response.data;
};

const createPublication = async (
  data: requestPublicationTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handleRequest("/admin/publications/", data, currentToken);
  return res.data;
};

const createPublicationAdmin = async (
  data: requestPublicationTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  createSupplierAdminSchema.parse(data);
  const res = await handleRequest("/publication/", data, currentToken);

  return res.data;
};

const editPublication = async (
  data: editPublicationTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handlePatchRequest(
    `/admin/publications/${domain}`,
    data,
    currentToken,
  );
  return res.data;
};

const deletePublication = async (slug: string): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!slug || typeof slug !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.delete<SuccesType>(`/${slug}`, {
    headers,
  });
  return res.data;
};

const createArticle = async (data: FormData): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handleRequest(`${domain}/articles`, data, currentToken);
  return res;
};

const apiClientSupplier = createApiClient("admin/suppliers");

const getSuppliers = async (): Promise<supplierTypes[]> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const suppliers = await handleGetRequest<supplierTypes[]>(
    apiClientSupplier,
    "/",
    currentToken,
  );
  return suppliers;
};

const getSupplier = async (supplier_name: string): Promise<supplierTypes> => {
  const supplier = await handleGetRequest<supplierTypes>(
    apiClientSupplier,
    `/${supplier_name}`,
  );
  return supplier;
};

const createSupplier = async (name: string): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handleRequest("/suppliers/", { name }, currentToken);
  return res;
};

const editSupplier = async (data: editSupplierTypes): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const res = await handlePatchRequest(
    `/admin/suppliers/${data.name}`,
    data,
    currentToken,
  );
  return res;
};

const deleteSupplier = async (name: string): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!name || typeof name !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientSupplier.delete<SuccesType>(
    `/${encodeURIComponent(name)}`,
    { headers },
  );
  return res.data;
};

const getOrCreateSupplier = async (name: string): Promise<supplierTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  if (!name || typeof name !== "string") {
    throw new Error(
      "El nombre del proveedor es obligatorio y debe ser un string.",
    );
  }

  const supplier = await handleGetRequest<supplierTypes>(
    apiClientSupplier,
    `/get/${name}`,
    currentToken,
  );
  return supplier;
};

const editMetadata = async (
  editMetadataRequest: editMetadataTypes,
): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.patch<string>(
    `${domain}/articles/${editMetadataRequest.article_slug}/edit`,
    editMetadataRequest.data,
    { headers },
  );

  return res.data;
};

const getPublicationStyle = async (): Promise<PublicationTheme> => {
  const res = await handleGetRequest<PublicationTheme>(
    apiClient,
    `/admin/publications/${domain}/theme`,
  );
  return res;
};

export const handlePutFileRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const addLogoUrl = async (data: addLogoUrlTypes): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/publications/${domain}/logo/`;
  const res = await handlePutFileRequest(path, data.file, currentToken);
  return res;
};

const addFavicon = async (data: addLogoUrlTypes): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/publications/${domain}/favicon`;
  const res = await handlePutFileRequest(path, data.file, currentToken);
  return res;
};

const addSupplierLogo = async (
  addSupplierLogoRequest: addSupplierLogoTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const path = `/admin/suppliers/${addSupplierLogoRequest.supplier_slug}/logo`;
  const res = await handlePutFileRequest(
    path,
    addSupplierLogoRequest.data.file,
    currentToken,
  );
  return res;
};

const addBanner = async (
  addBannerRequest: addBannerTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.patch<SuccesType>(
    `/admin/publications/${domain}/banner/${addBannerRequest.banner_number}`,
    addBannerRequest.data,
    { headers },
  );

  return res.data;
};

const addBannerImg = async (addBannerImgRequest: any): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const path = `/admin/publications/${domain}/banner/${addBannerImgRequest.banner_number}/image`;

  const res = await apiClient.put<any>(path, addBannerImgRequest.data, {
    headers,
  });
  return res.data;
};

const getRoles = async (): Promise<userRoleTypes[]> => {
  const currentToken = useToken();
  const res = await handleGetRequest<userRoleTypes[]>(
    apiClient,
    `${domain}/role/all`,
    currentToken || undefined,
  );
  return res;
};

const getRolesByUser = async (): Promise<UsersCollection> => {
  const currentToken = useToken();
  const res = await handleGetRequest<UsersCollection>(
    apiClient,
    `${domain}/role/by_user`,
    currentToken || undefined,
  );
  return res;
};

const getPublicationFooter = async (): Promise<PublicationPoliciesTypes> => {
  const response = await apiClientPublication.get<PublicationPoliciesTypes>(
    `/${domain}/footer`,
  );
  return response.data;
};

const getInitialFooter = async (): Promise<initialFooterTypes> => {
  const response = await apiClientPublication.get<initialFooterTypes>(
    `/${domain}/footer_initial`,
  );
  return response.data;
};

const getPublicationContact = async (): Promise<PublicationContactTypes> => {
  const response = await apiClientPublication.get<PublicationContactTypes>(
    `/${domain}/contact`,
  );
  return response.data;
};

const getPublicationBanners = async (): Promise<PublicationBannersTypes> => {
  const response = await apiClientPublication.get<PublicationBannersTypes>(
    `/${domain}/banners`,
  );
  return response.data;
};

const editPublicationFooter = async (
  editFooterRequest: editFooterTypes,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.patch<SuccesType>(
    `/${domain}/footer`,
    editFooterRequest,
    { headers },
  );
  return res.data;
};

const uploadHowToReviewFile = async (file: FormData): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.put<SuccesType>(
    `/${domain}/how-to-review/upload`,
    file,
    { headers },
  );
  return res.data;
};

const deleteHowToReviewFile = async (): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClientPublication.delete<SuccesType>(
    `/${domain}/how-to-review/delete`,
    { headers },
  );
  return res.data;
}

const reparseWord = async (
  article_id: number,
  url: string,
): Promise<SuccesType> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const body = new URLSearchParams();
  body.append("url", url);

  const res = await apiAmsBase.post<SuccesType>(
    `/articles/${article_id}/reparse-from-url?pub_slug=${domain}`,
    body,
    { headers },
  );
  return res.data;
};


const adminService = {
  reparseWord,
  getPublications,
  getPublicationBySlug,
  createPublication,
  createPublicationAdmin,
  editPublication,
  createArticle,
  deleteSupplier,
  getSuppliers,
  addFavicon,
  addSupplierLogo,
  getSupplier,
  createSupplier,
  editSupplier,
  deletePublication,
  editMetadata,
  addLogoUrl,
  addBanner,
  addBannerImg,
  uploadHowToReviewFile,
  getRoles,
  getRolesByUser,
  getOrCreateSupplier,
  getPublicationFooter,
  getInitialFooter,
  getPublicationContact,
  getPublicationBanners,
  editPublicationFooter,
  getPublicationStyle,
  deleteHowToReviewFile,
};

export default adminService;
